<template>
  <div class="autocomplete-container">
    <!-- optional indicators -->
    <i class="fa fa-spinner fa-spin" v-if="loading"></i>
    <template v-else>
      <i class="fa fa-search" v-show="isEmpty"></i>
      <i class="fa fa-times" v-show="isDirty" @click="reset"></i>
    </template>

    <!-- the input field -->
    <input
      :label="label"
      class="form-control"
      :placeholder="placeholder"
      v-model="query"
      autocomplete="off"
      :name="name"
      :id="name"
      :class="inputClass"
      @focus="onInputSelect"
      @keydown.down="down"
      @keydown.up="up"
      v-on:keydown.enter.prevent="onSelect"
      @keydown.esc="reset"
      @blur="reset"
      @input="updateTimeout"
      :hint="hint"
      :persistent-hint="persistentHint"
    />

    <label class="indigo--text details-label text-none" v-if="!hideDetails">{{ searchDescription }}</label>
    <!-- the list -->
    <ul v-show="items" class="pl-0">
      <li
        v-for="( item, index ) in items"
        :key="index"
        :class="activeClass(index)"
        class="auto-label"
        @mousedown="onSelect"
        @mousemove="setActive(index)">
        <strong v-text="item[criteria]"></strong>
        <span v-text="item[description]"></span>
      </li>
    </ul>
  </div>
</template>
<script>
export default {
  name: 'autocomplete',
  data () {
    return {
      items: [],
      query: '',
      searchDescription: '',
      current: -1,
      loading: false,
      cancelSearch: false,
      itemSelected: false,
      data: {},
      limit: 5,
      minChars: 1,
      selectFirst: true,
      queryParamName: 'search'
    }
  },
  props: {
    value: {
      type: String
    },
    inputClass: {
      type: String
    },
    name: {
      type: String
    },
    url: {
      type: String
    },
    criteria: {
      type: String
    },
    description: {
      type: String
    },
    placeholder: {
      type: String,
      default: ''
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    prepareResponseData: {
      type: Function
    },
    label: {
      type: String
    }
  },
  computed: {
    isEmpty () {
      return !this.query
    },
    isDirty () {
      return !!this.query
    }
  },
  methods: {
    updateTimeout () {
      clearTimeout(this.sendFilter)
      this.sendFilter = setTimeout(() => {
        this.update()
      }, 300)
    },
    isFunction (functionToCheck) {
      return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]'
    },
    update () {
      this.cancel()
      this.itemSelected = false

      if (this.cancelSearch) {
        return
      }

      if (!this.query) {
        return this.reset()
      }

      if (this.minChars && this.query.length < this.minChars) {
        return
      }

      this.$emit('input', this.query)
      this.loading = true

      this.fetch().then((response) => {
        if (response && this.query) {
          let data = response.data
          data = this.isFunction(this.prepareResponseData) ? this.prepareResponseData(data) : this.prepareResponseDataDefault(data)
          this.items = this.limit ? data.slice(0, this.limit) : data
          this.current = -1
          this.loading = false
          if (this.selectFirst) {
            this.down()
          }
        }
      })
    },
    fetch () {
      const url = this.queryParamName
        ? this.url
        : this.url + this.query

      const params = this.queryParamName
        ? Object.assign({ [this.queryParamName]: this.query }, this.data)
        : this.data

      let cancel = new Promise((resolve) => (this.cancel = resolve))
      let request = this.$http.get(url, { params })

      return Promise.race([cancel, request])
    },
    cancel () {
      // used to 'cancel' previous searches
    },
    reset () {
      if (this.itemSelected) { return }

      if (!this.cancelSearch) {
        this.resetField()
      }
    },
    resetField () {
      this.items = []
      this.loading = false
      this.searchDescription = ''
      this.$emit('resolved', null)
    },
    resetContents () {
      this.query = ''
      this.resetField()
    },
    setContents (contents) {
      this.query = contents
    },
    setActive (index) {
      this.current = index
      // this.cancelSearch = true
      this.query = this.items[index][this.criteria]
      this.$emit('resolved', this.items[index])
    },
    activeClass (index) {
      return {
        'indigo lighten-1 white--text': this.current === index
      }
    },
    up () {
      if (this.current > 0) {
        this.current--
      } else if (this.current === -1) {
        this.current = this.items.length - 1
      } else {
        this.current = -1
      }
    },
    down () {
      if (this.current < this.items.length - 1) {
        this.current++
      } else {
        this.current = -1
      }
    },
    onInputSelect () {
      this.cancelSearch = false
    },
    onSelect () {
      if (this.current !== -1) {
        // this.cancelSearch = true
        this.$emit('resolved', this.items[this.current])
        this.query = this.items[this.current][this.criteria] + ''
        this.searchDescription = this.items[this.current][this.description] + ''
        this.items = []

        this.$emit('input', this.query)
        this.itemSelected = true
      }
    },
    prepareResponseDataDefault (data) {
      return data
    },
    validate () {
      this.$validator.validateAll().then(result => {
        if (result) {
        }
      })
    }
  }
}
</script>
<style lang="scss" type="text/scss">
@import '~@/assets/scss/variables.scss';

.autocomplete-container {
  position: relative;

  .fa-times {
    cursor: pointer;
  }
  i {
    float: right;
    position: relative;
    top: 30px;
    right: 29px;
    opacity: 0.4;
  }
  ul {
    position: absolute;
    padding: 0;
    margin-top: -20px;
    min-width: 100%;
    background-color: #fff;
    list-style: none;
    border-radius: 4px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);
    z-index: 1000;
    li {
      padding: 10px 16px;
      border-bottom: 1px solid #ccc;
      cursor: pointer;
    }
    li:first-child {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }
    li:last-child {
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      border-bottom: 0;
    }
  }

  .auto-label{
    font-size: 14px;
    font-weight: 400;
  }

  span {
    display: block;
  }
  .name {
    font-weight: 400;
    font-size: 12px;
  }
  .screen-name {
    font-style: italic;
  }
}
</style>



// WEBPACK FOOTER //
// Autocomplete.vue?73e58eba